/**
 * TikTok 通知抽屉外部状态：避免 TopBar/Provider 重挂载把 useState 重置成关闭并清空列表。
 */

let noticeChannel = null; // 'platform' | 'system' | null
let noticeLockedChannel = 'platform';
/** @type {any[]} */
let alertItems = [];
let alertCounts = { platform: 0, system: 0 };
/** 本会话已打开过的通道：角标只保留业务待办 */
let channelSeen = { platform: false, system: false };
let alertsShopId = null;

const channelListeners = new Set();
const alertListeners = new Set();

function emit(listeners) {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function normalizeChannel(channel) {
  return channel === 'system' ? 'system' : 'platform';
}

function isBusinessTodo(item) {
  return item?.source === 'business';
}

/** 角标：未打开过的通道计全部未读；打开过则只计业务待办。 */
export function computeNoticeBadgeCounts(items, seen = {}) {
  const list = Array.isArray(items) ? items : [];
  const count = (channel) => list.filter((item) => {
    if (!item?.id) return false;
    const itemCh = item.channel || 'platform';
    if (itemCh !== channel) return false;
    if (seen[channel] && !isBusinessTodo(item)) return false;
    return true;
  }).length;
  return { platform: count('platform'), system: count('system') };
}

function refreshAlertCounts() {
  const next = computeNoticeBadgeCounts(alertItems, channelSeen);
  const countsChanged = alertCounts.platform !== next.platform || alertCounts.system !== next.system;
  if (!countsChanged) return false;
  alertCounts = next;
  return true;
}

export function getTiktokNoticeChannel() {
  return noticeChannel;
}

export function getTiktokNoticeLockedChannel() {
  return noticeChannel || noticeLockedChannel;
}

export function subscribeTiktokNoticeChannel(listener) {
  channelListeners.add(listener);
  return () => channelListeners.delete(listener);
}

export function openTiktokNotice(channel) {
  const next = normalizeChannel(channel);
  noticeLockedChannel = next;
  noticeChannel = next;
  emit(channelListeners);
}

/** 仅允许「关闭」按钮调用 */
export function closeTiktokNotice() {
  if (noticeChannel == null) return;
  noticeChannel = null;
  emit(channelListeners);
}

export function getTiktokNoticeAlertItems() {
  return alertItems;
}

export function getTiktokNoticeAlertCounts() {
  return alertCounts;
}

export function subscribeTiktokNoticeAlerts(listener) {
  alertListeners.add(listener);
  return () => alertListeners.delete(listener);
}

/**
 * 打开抽屉：该通道非待办视为已读，角标只留业务待办。
 * @returns {() => void} 失败时调用以恢复角标
 */
export function markTiktokNoticeChannelSeen(channel) {
  const ch = normalizeChannel(channel);
  const prevSeen = Boolean(channelSeen[ch]);
  const prevCounts = { ...alertCounts };
  channelSeen = { ...channelSeen, [ch]: true };
  if (refreshAlertCounts()) {
    emit(alertListeners);
  }
  return () => {
    channelSeen = { ...channelSeen, [ch]: prevSeen };
    alertCounts = prevCounts;
    emit(alertListeners);
  };
}

export function setTiktokNoticeAlerts(itemsOrBundle, { shopId } = {}) {
  let nextItems = [];
  if (Array.isArray(itemsOrBundle)) {
    nextItems = itemsOrBundle;
  } else if (itemsOrBundle && Array.isArray(itemsOrBundle.items)) {
    nextItems = itemsOrBundle.items;
  }

  if (shopId == null || shopId === '') {
    channelSeen = { platform: false, system: false };
    alertsShopId = null;
  } else if (String(shopId) !== String(alertsShopId)) {
    channelSeen = { platform: false, system: false };
    alertsShopId = shopId;
  }

  const itemsChanged = nextItems.length !== alertItems.length
    || nextItems.some((item, idx) => item?.id !== alertItems[idx]?.id);
  const nextCounts = computeNoticeBadgeCounts(nextItems, channelSeen);
  const countsChanged = alertCounts.platform !== nextCounts.platform
    || alertCounts.system !== nextCounts.system;

  if (!itemsChanged && !countsChanged) return;

  alertItems = nextItems;
  alertCounts = nextCounts;
  emit(alertListeners);
}

/** 关闭抽屉时禁止用空列表覆盖已有角标/缓存 */
export function setTiktokNoticeAlertsSafe(itemsOrBundle, { allowEmpty = true, shopId } = {}) {
  let nextItems = [];
  if (Array.isArray(itemsOrBundle)) {
    nextItems = itemsOrBundle;
  } else if (itemsOrBundle && Array.isArray(itemsOrBundle.items)) {
    nextItems = itemsOrBundle.items;
  }
  if (!allowEmpty && nextItems.length === 0 && alertItems.length > 0) {
    return;
  }
  setTiktokNoticeAlerts(nextItems, { shopId });
}
